'use client'

import { Instagram, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

const INSTAGRAM_URL = 'https://www.instagram.com/glutenvrijadvies/'

export const InstagramFeed = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldLoad(true)
            observer.disconnect()
            break
          }
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!shouldLoad) return
    const w = window as any
    if (!w.instgrm) {
      const existing = document.querySelector<HTMLScriptElement>('script[src="https://www.instagram.com/embed.js"]')
      if (!existing) {
        const script = document.createElement('script')
        script.src = 'https://www.instagram.com/embed.js'
        script.async = true
        document.body.appendChild(script)
      }
    } else {
      w.instgrm.Embeds.process()
    }

    // Instagram injects an iframe without a title; patch it in for a11y.
    const container = containerRef.current
    if (!container) return
    const mo = new MutationObserver(() => {
      const iframe = container.querySelector('iframe')
      if (iframe && !iframe.title) {
        iframe.title = 'Instagram-berichten van @glutenvrijadvies'
      }
    })
    mo.observe(container, { childList: true, subtree: true })
    return () => mo.disconnect()
  }, [shouldLoad])

  return (
    <div className="flex h-full flex-col">
      <div className="mb-8 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Instagram</p>
        <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
          Glutenvrij Advies
        </h2>
        <p className="max-w-lg text-muted-foreground">
          Volg ons op Instagram voor dagelijkse tips over glutenvrij eten en een gezonde leefstijl.
        </p>
      </div>

      <div
        ref={containerRef}
        className="relative min-h-[400px] max-h-[580px] overflow-hidden [&_iframe]:!w-full [&_iframe]:!max-w-full [&_iframe]:!min-w-0 [&_iframe]:!m-0 [&_iframe]:!rounded-none"
      >
        {shouldLoad && (
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={INSTAGRAM_URL}
            data-instgrm-version="14"
            style={{
              background: 'transparent',
              border: 0,
              margin: 0,
              padding: 0,
              width: '100%',
              maxWidth: '100%',
            }}
          />
        )}
      </div>

      <div className="mt-auto pt-8">
        <Button asChild variant="outline" className="rounded-full">
          <Link
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            <Instagram className="h-4 w-4" />
            Volg op Instagram
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}
